import { useId } from "react";
import type { HttpAuthType } from "@/types/api";
import { SelectDropdown } from "./SelectDropdown";
import {
  FieldRow,
  FieldRowControl,
  FormGroup,
  FormSection,
  HelpText,
  Input,
  Label,
  SectionTitle,
} from "./styles";

interface HttpAuthFieldsProps {
  authType: HttpAuthType | "none";
  setAuthType: (value: HttpAuthType | "none") => void;
  bearerToken: string;
  setBearerToken: (value: string) => void;
  basicUsername: string;
  setBasicUsername: (value: string) => void;
  basicPassword: string;
  setBasicPassword: (value: string) => void;
  apiKeyKey: string;
  setApiKeyKey: (value: string) => void;
  apiKeyParamName: string;
  setApiKeyParamName: (value: string) => void;
  headerName: string;
  setHeaderName: (value: string) => void;
  headerValue: string;
  setHeaderValue: (value: string) => void;
}

const AUTH_OPTIONS: { value: HttpAuthType | "none"; label: string }[] = [
  { value: "none", label: "None" },
  { value: "bearer", label: "Bearer Token" },
  { value: "basic", label: "Basic Auth" },
  { value: "api_key", label: "API Key (query param)" },
  { value: "header", label: "Custom Header" },
];

function BearerFields({
  token,
  setToken,
}: {
  token: string;
  setToken: (v: string) => void;
}) {
  const id = useId();
  return (
    <FormGroup>
      <Label htmlFor={id}>Token *</Label>
      <Input
        id={id}
        type="password"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="Bearer token"
        required
      />
      <HelpText>{"Sent as Authorization: Bearer <token>"}</HelpText>
    </FormGroup>
  );
}

function BasicFields({
  username,
  setUsername,
  password,
  setPassword,
}: {
  username: string;
  setUsername: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
}) {
  const usernameId = useId();
  const passwordId = useId();
  return (
    <>
      <FormGroup>
        <Label htmlFor={usernameId}>Username *</Label>
        <Input
          id={usernameId}
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="username"
          required
        />
      </FormGroup>
      <FormGroup>
        <Label htmlFor={passwordId}>Password *</Label>
        <Input
          id={passwordId}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="password"
          required
        />
        <HelpText>{"Sent as Authorization: Basic <base64>"}</HelpText>
      </FormGroup>
    </>
  );
}

function ApiKeyFields({
  apiKey,
  setApiKey,
  paramName,
  setParamName,
}: {
  apiKey: string;
  setApiKey: (v: string) => void;
  paramName: string;
  setParamName: (v: string) => void;
}) {
  const keyId = useId();
  const paramId = useId();
  return (
    <>
      <FormGroup>
        <Label htmlFor={keyId}>API Key *</Label>
        <Input
          id={keyId}
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="your-api-key"
          required
        />
      </FormGroup>
      <FormGroup>
        <Label htmlFor={paramId}>Parameter Name</Label>
        <Input
          id={paramId}
          type="text"
          value={paramName}
          onChange={(e) => setParamName(e.target.value)}
          placeholder="api_key"
        />
        <HelpText>
          {`Appended as query parameter: ?${paramName || "api_key"}=<key>`}
        </HelpText>
      </FormGroup>
    </>
  );
}

function HeaderFields({
  name,
  setName,
  value,
  setValue,
}: {
  name: string;
  setName: (v: string) => void;
  value: string;
  setValue: (v: string) => void;
}) {
  const nameId = useId();
  const valueId = useId();
  return (
    <>
      <FormGroup>
        <Label htmlFor={nameId}>Header Name *</Label>
        <Input
          id={nameId}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="X-Api-Key"
          required
        />
      </FormGroup>
      <FormGroup>
        <Label htmlFor={valueId}>Header Value *</Label>
        <Input
          id={valueId}
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="secret-value"
          required
        />
      </FormGroup>
    </>
  );
}

export function HttpAuthFields({
  authType,
  setAuthType,
  bearerToken,
  setBearerToken,
  basicUsername,
  setBasicUsername,
  basicPassword,
  setBasicPassword,
  apiKeyKey,
  setApiKeyKey,
  apiKeyParamName,
  setApiKeyParamName,
  headerName,
  setHeaderName,
  headerValue,
  setHeaderValue,
}: HttpAuthFieldsProps) {
  return (
    <FormSection>
      <SectionTitle>Authentication</SectionTitle>

      <FieldRow>
        <Label as="span">Auth type</Label>
        <FieldRowControl>
          <SelectDropdown
            value={authType}
            options={AUTH_OPTIONS}
            onChange={setAuthType}
          />
        </FieldRowControl>
      </FieldRow>

      {authType === "bearer" && (
        <BearerFields token={bearerToken} setToken={setBearerToken} />
      )}
      {authType === "basic" && (
        <BasicFields
          username={basicUsername}
          setUsername={setBasicUsername}
          password={basicPassword}
          setPassword={setBasicPassword}
        />
      )}
      {authType === "api_key" && (
        <ApiKeyFields
          apiKey={apiKeyKey}
          setApiKey={setApiKeyKey}
          paramName={apiKeyParamName}
          setParamName={setApiKeyParamName}
        />
      )}
      {authType === "header" && (
        <HeaderFields
          name={headerName}
          setName={setHeaderName}
          value={headerValue}
          setValue={setHeaderValue}
        />
      )}
    </FormSection>
  );
}
